import getRouterBasename from '@/lib/router';
import {
  isSharePath,
  pathWithoutBasename,
  useBrowserPathname
} from '@/lib/share';
import App from 'App';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { rememberAuthReturnTo } from '@/lib/authRedirect';
import {
  getHtmlLanguage,
  getStoredAppLanguage,
  normalizeAppLanguage
} from '@/lib/language';

import {
  useApi,
  useAuth,
  useChatInteract,
  useConfig
} from '@chainlit/react-client';

function isPublicPath(pathname: string): boolean {
  const path = pathWithoutBasename(pathname);
  return (
    path === '/login' || path === '/login/callback' || isSharePath(pathname)
  );
}

export default function AppWrapper() {
  const { isAuthenticated, isReady } = useAuth();
  const { language: configuredLanguage } = useConfig();
  const { i18n } = useTranslation();
  const { windowMessage } = useChatInteract();
  const pathname = useBrowserPathname();
  const languageInUse = getStoredAppLanguage(
    normalizeAppLanguage(configuredLanguage)
  );
  const shouldRedirectToLogin =
    isReady && !isAuthenticated && !isPublicPath(pathname);

  useEffect(() => {
    document.documentElement.lang = getHtmlLanguage(
      i18n.resolvedLanguage || languageInUse
    );
  }, [i18n.resolvedLanguage, languageInUse]);

  function handleChangeLanguage(languageBundle: any): void {
    i18n.addResourceBundle(
      languageInUse,
      'translation',
      languageBundle,
      true,
      true
    );
    i18n.changeLanguage(languageInUse);
  }

  const { data: translations } = useApi<any>(
    isAuthenticated ? `/project/translations?language=${languageInUse}` : null
  );

  useEffect(() => {
    if (!translations) return;
    handleChangeLanguage(translations.translation);
  }, [translations]);

  useEffect(() => {
    const handleWindowMessage = (event: MessageEvent) => {
      windowMessage(event.data);
    };
    window.addEventListener('message', handleWindowMessage);
    return () => window.removeEventListener('message', handleWindowMessage);
  }, [windowMessage]);

  useEffect(() => {
    if (!shouldRedirectToLogin) return;
    const returnTo = `${pathWithoutBasename(pathname)}${window.location.search}${window.location.hash}`;
    rememberAuthReturnTo(returnTo);
    window.location.href = getRouterBasename() + '/login';
  }, [pathname, shouldRedirectToLogin]);

  if (shouldRedirectToLogin) return null;
  return <App />;
}
